/* ============================================================
   SpeedFly V6 — Header JS
   Sticky scroll detection, predictive search, mobile nav drawer
   Custom element: <sticky-header>
   Zero jQuery dependency
   ============================================================ */

(function () {
  'use strict';

  /* ========================================================
     STICKY HEADER — Custom Element
     Adds .is-scrolled class when user scrolls past threshold
     ======================================================== */
  class StickyHeader extends HTMLElement {
    constructor() {
      super();
      this.scrollThreshold = 50;
      this.isScrolled = false;
      this.onScroll = this.onScroll.bind(this);
    }

    connectedCallback() {
      if (this.classList.contains('header--sticky')) {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    onScroll() {
      const scrolled = window.scrollY > this.scrollThreshold;
      if (scrolled !== this.isScrolled) {
        this.isScrolled = scrolled;
        this.classList.toggle('is-scrolled', scrolled);
      }
    }
  }

  if (!customElements.get('sticky-header')) {
    customElements.define('sticky-header', StickyHeader);
  }

  /* ========================================================
     PREDICTIVE SEARCH — Enhanced
     Toggle overlay, debounced fetch, render results,
     keyboard navigation, loading skeleton, view-all link
     ======================================================== */
  class PredictiveSearch {
    constructor() {
      this.toggle = document.querySelector('[data-search-toggle]');
      this.overlay = document.querySelector('[data-search-overlay]');
      this.closeBtn = document.querySelector('[data-search-close]');
      this.input = document.querySelector('[data-predictive-search-input]');
      this.resultsContainer = document.querySelector('[data-predictive-search-results]');

      if (!this.overlay || !this.input) return;

      this.searchUrl = window.routes?.search_url || '/search';
      this.isOpen = false;
      this.abortController = null;
      this.cache = {};
      this.activeIndex = -1;
      this.resultLinks = [];

      this.bindEvents();
    }

    bindEvents() {
      if (this.toggle) {
        this.toggle.addEventListener('click', () => this.open());
      }

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      /* Click outside to close */
      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay || e.target.closest('.search-overlay__inner') === null) {
          /* Don't close if clicking inside results */
        }
      });

      /* Escape key */
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) this.close();
      });

      /* Input — debounced search */
      this.input.addEventListener(
        'input',
        SpeedFly.debounce(() => this.onInput(), 300)
      );

      /* Keyboard navigation in results */
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          this.navigateResults(1);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          this.navigateResults(-1);
        } else if (e.key === 'Enter' && this.activeIndex >= 0) {
          e.preventDefault();
          this.resultLinks[this.activeIndex]?.click();
        }
      });

      /* Submit form normally if enter pressed with query */
      this.input.closest('form')?.addEventListener('submit', (e) => {
        if (!this.input.value.trim()) {
          e.preventDefault();
        }
      });
    }

    open() {
      this.overlay.hidden = false;
      this.isOpen = true;
      if (this.toggle) this.toggle.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => {
        this.input.focus();
      });
    }

    close() {
      this.overlay.hidden = true;
      this.isOpen = false;
      if (this.toggle) this.toggle.setAttribute('aria-expanded', 'false');
      this.clearResults();
      this.input.value = '';
      this.activeIndex = -1;
      if (this.abortController) this.abortController.abort();
    }

    navigateResults(delta) {
      if (!this.resultLinks.length) return;
      this.activeIndex += delta;
      if (this.activeIndex < 0) this.activeIndex = this.resultLinks.length - 1;
      if (this.activeIndex >= this.resultLinks.length) this.activeIndex = 0;

      this.resultLinks.forEach((link, i) => {
        link.classList.toggle('is-focused', i === this.activeIndex);
      });

      this.resultLinks[this.activeIndex]?.scrollIntoView({ block: 'nearest' });
    }

    showLoading() {
      if (!this.resultsContainer) return;
      this.resultsContainer.innerHTML = `
        <div class="search-results__loading">
          <div class="search-results__skeleton">
            <div class="skeleton" style="width:50px;height:50px;border-radius:var(--radius-sm)"></div>
            <div style="flex:1">
              <div class="skeleton" style="width:70%;height:14px;margin-bottom:8px"></div>
              <div class="skeleton" style="width:40%;height:12px"></div>
            </div>
          </div>
          <div class="search-results__skeleton">
            <div class="skeleton" style="width:50px;height:50px;border-radius:var(--radius-sm)"></div>
            <div style="flex:1">
              <div class="skeleton" style="width:60%;height:14px;margin-bottom:8px"></div>
              <div class="skeleton" style="width:35%;height:12px"></div>
            </div>
          </div>
          <div class="search-results__skeleton">
            <div class="skeleton" style="width:50px;height:50px;border-radius:var(--radius-sm)"></div>
            <div style="flex:1">
              <div class="skeleton" style="width:80%;height:14px;margin-bottom:8px"></div>
              <div class="skeleton" style="width:45%;height:12px"></div>
            </div>
          </div>
        </div>`;
    }

    async onInput() {
      const query = this.input.value.trim();

      if (query.length < 2) {
        this.clearResults();
        return;
      }

      /* Check cache */
      if (this.cache[query]) {
        this.renderResults(this.cache[query], query);
        return;
      }

      /* Show loading skeleton */
      this.showLoading();

      /* Abort previous request */
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      try {
        const url = `${this.searchUrl}/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,article,page&resources[limit]=6`;

        const res = await fetch(url, {
          signal: this.abortController.signal,
          headers: { Accept: 'application/json' },
        });

        if (!res.ok) throw new Error('Search failed');

        const data = await res.json();
        this.cache[query] = data;
        this.renderResults(data, query);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('[SpeedFly] Predictive search error:', err);
          this.clearResults();
        }
      }
    }

    renderResults(data, query) {
      if (!this.resultsContainer) return;

      const resources = data.resources?.results;
      if (!resources) {
        this.clearResults();
        return;
      }

      const products = resources.products || [];
      const articles = resources.articles || [];
      const pages = resources.pages || [];

      if (!products.length && !articles.length && !pages.length) {
        this.resultsContainer.innerHTML =
          '<p class="search-overlay__empty text-sm text-light" style="padding:var(--spacing-md) 0">' +
          (window.cartStrings?.noResults || 'No results found') +
          '</p>';
        this.resultLinks = [];
        this.activeIndex = -1;
        return;
      }

      let html = '';

      /* Products */
      if (products.length) {
        html += '<div class="search-results__section">';
        html += '<p class="search-results__label text-xs text-light uppercase tracking-wide mb-sm">' +
          (window.searchStrings?.products || 'Products') + '</p>';
        html += '<div class="search-results__grid">';
        products.forEach((p) => {
          const img = p.image
            ? `<img src="${p.image}" alt="${this.escapeHtml(p.title)}" class="search-results__image" loading="lazy" width="50" height="50">`
            : '<div class="search-results__image-placeholder"></div>';
          const price = p.price
            ? `<span class="search-results__price">${(parseFloat(String(p.price).replace(',', '.')) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} \u20ac</span>`
            : '';
          html += `
            <a href="${p.url}" class="search-results__item" data-search-result>
              ${img}
              <div>
                <p class="search-results__title">${this.escapeHtml(p.title)}</p>
                ${price}
              </div>
            </a>`;
        });
        html += '</div></div>';
      }

      /* Articles */
      if (articles.length) {
        html += '<div class="search-results__section">';
        html += '<p class="search-results__label text-xs text-light uppercase tracking-wide mb-sm">' +
          (window.searchStrings?.articles || 'Articles') + '</p>';
        articles.forEach((a) => {
          html += `<a href="${a.url}" class="search-results__item" data-search-result><div><p class="search-results__title">${this.escapeHtml(a.title)}</p></div></a>`;
        });
        html += '</div>';
      }

      /* Pages */
      if (pages.length) {
        html += '<div class="search-results__section">';
        html += '<p class="search-results__label text-xs text-light uppercase tracking-wide mb-sm">' +
          (window.searchStrings?.pages || 'Pages') + '</p>';
        pages.forEach((pg) => {
          html += `<a href="${pg.url}" class="search-results__item" data-search-result><div><p class="search-results__title">${this.escapeHtml(pg.title)}</p></div></a>`;
        });
        html += '</div>';
      }

      /* View all results link */
      if (query) {
        html += `<div class="search-results__viewall">
          <a href="${this.searchUrl}?q=${encodeURIComponent(query)}" class="search-results__viewall-link" data-search-result>
            ${window.searchStrings?.viewAll || 'View all results'} &rarr;
          </a>
        </div>`;
      }

      this.resultsContainer.innerHTML = html;

      /* Collect navigable links */
      this.resultLinks = Array.from(this.resultsContainer.querySelectorAll('[data-search-result]'));
      this.activeIndex = -1;
    }

    clearResults() {
      if (this.resultsContainer) this.resultsContainer.innerHTML = '';
      this.resultLinks = [];
      this.activeIndex = -1;
    }

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
  }

  /* ========================================================
     MOBILE NAVIGATION
     Drawer open/close, 3-level expand/collapse with aria
     ======================================================== */
  class MobileNav {
    constructor() {
      this.toggleBtn = document.querySelector('[data-mobile-menu-toggle]');
      this.drawer = document.querySelector('[data-mobile-menu-drawer]');
      this.overlay = document.querySelector('[data-mobile-menu-overlay]');
      this.closeBtn = document.querySelector('[data-mobile-menu-close]');

      if (!this.drawer) return;

      this.isOpen = false;
      this.previousFocus = null;
      this.bindEvents();
    }

    bindEvents() {
      if (this.toggleBtn) {
        this.toggleBtn.addEventListener('click', () => this.open());
      }

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) this.close();
      });

      this.drawer.querySelectorAll('.mobile-nav__toggle').forEach((toggle) => {
        toggle.addEventListener('click', () => this.toggleSubmenu(toggle));
      });
    }

    open() {
      this.previousFocus = document.activeElement;
      this.isOpen = true;

      this.drawer.classList.add('is-active');
      if (this.overlay) {
        this.overlay.classList.add('is-active');
        this.overlay.setAttribute('aria-hidden', 'false');
      }
      if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';

      requestAnimationFrame(() => {
        this.closeBtn?.focus();
      });
    }

    close() {
      this.isOpen = false;

      this.drawer.classList.remove('is-active');
      if (this.overlay) {
        this.overlay.classList.remove('is-active');
        this.overlay.setAttribute('aria-hidden', 'true');
      }
      if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';

      if (this.previousFocus) {
        this.previousFocus.focus();
        this.previousFocus = null;
      }
    }

    toggleSubmenu(toggle) {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      const submenu = toggle.parentElement.nextElementSibling;

      if (!submenu) return;

      toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');

      if (expanded) {
        submenu.hidden = true;
      } else {
        submenu.hidden = false;
      }
    }
  }

  /* === INIT === */
  document.addEventListener('DOMContentLoaded', () => {
    new PredictiveSearch();
    new MobileNav();
  });
})();
